import { useEffect, useMemo, useState } from 'react';
import NotificationComposerModal, { ComposerRecipient } from '@/components/notifications/NotificationComposerModal';
import whatsappService, { TemplateItem, WhatsAppEventType } from '@/services/whatsappService';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import UserProfileDialog from '@/components/Pages/UserManagement/components/UserProfileDialog';
import UserService from '@/components/Pages/UserManagement/services/userService';
import type { Usuario } from '@/auth/types';
import type { UpdateUserProfileData } from '@/components/Pages/UserManagement/types';

type TramiteLike = {
  id: number | string;
  compradores?: Array<any>;
  vendedores?: Array<any>;
  otorgantes?: Array<any>;
  responsables?: Array<any>;
};

type RecipientGroup = 'compradores' | 'vendedores' | 'otorgantes' | 'responsables';

type WhatsAppEventModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tramite: TramiteLike;
  onTramiteUpdated?: () => Promise<void> | void;
  eventType: WhatsAppEventType;
  title: string;
  description: string;
  contextOverrides?: Record<string, unknown>;
  recipientGroups?: RecipientGroup[];
  initialMessage?: string;
};

const normalizePhoneForDedupe = (value: string): string => value.replace(/\s+/g, '').trim();
const normalizePhone = (value: string): string => value.replace(/\D+/g, '');
const normalizeLinkedUserId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const isValidPhoneFormat = (value: string): boolean => {
  const digits = normalizePhone(value);
  if (!digits) return false;

  if (digits.startsWith('549') && digits.length >= 12 && digits.length <= 13) return true;
  if (digits.startsWith('54') && digits.length >= 11 && digits.length <= 12) return true;
  if (digits.startsWith('9') && digits.length >= 10 && digits.length <= 11) return true;
  if (digits.length >= 10 && digits.length <= 11) return true;

  return false;
};

const buildInitialRecipients = (tramite: TramiteLike, recipientGroups: RecipientGroup[]): ComposerRecipient[] => {
  const groupToRoleLabel: Record<RecipientGroup, string> = {
    compradores: 'Comprador',
    vendedores: 'Vendedor',
    otorgantes: 'Otorgante',
    responsables: 'Responsable',
  };

  const people = recipientGroups.flatMap((group) => {
    return (tramite[group] ?? []).map((entry) => ({ ...entry, group, role: groupToRoleLabel[group] }));
  });

  const recipients: ComposerRecipient[] = [];

  people.forEach((entry, index) => {
    const linkedUserId = normalizeLinkedUserId(entry?.user?.id ?? entry?.userId);
    const linkedProfile = entry?.user?.profile ?? entry?.profile ?? {};
    const name = (entry?.user?.name ?? entry?.nombre ?? `${entry.role} ${index + 1}`).toString();
    const rawPhone = (linkedProfile?.telefono ?? entry?.telefono ?? '').toString().trim();
    const normalized = normalizePhoneForDedupe(rawPhone);

    recipients.push({
      id: normalized
        ? `auto-${index}-${normalized}`
        : `auto-${index}-no-phone-${String(entry?.id ?? entry?.user?.id ?? index)}`,
      name,
      phone: rawPhone,
      selected: true,
      source: 'auto',
      linkedUserId,
      participantId: typeof entry?.id === 'number' ? entry.id : Number.isInteger(Number(entry?.id)) ? Number(entry.id) : undefined,
      participantType: entry?.group === 'compradores' ? 'comprador' : entry?.group === 'vendedores' ? 'vendedor' : undefined,
      tramiteId: Number.isInteger(Number(tramite.id)) ? Number(tramite.id) : undefined,
      linkedProfile: {
        dni: linkedProfile?.dni ?? null,
        telefono: linkedProfile?.telefono ?? null,
        direccion: linkedProfile?.direccion ?? null,
      },
      isPersistable: linkedUserId !== null,
    });
  });

  return recipients;
};

const WhatsAppEventModal = ({
  open,
  onOpenChange,
  tramite,
  onTramiteUpdated,
  eventType,
  title,
  description,
  contextOverrides = {},
  recipientGroups = ['compradores', 'vendedores', 'otorgantes', 'responsables'],
  initialMessage = '',
}: WhatsAppEventModalProps) => {
  const { errorAlert, successAlert, questionAlert } = useAlert();
  const { canManageUsers } = useAuthorization();

  const [recipients, setRecipients] = useState<ComposerRecipient[]>([]);
  const [template, setTemplate] = useState<TemplateItem | null>(null);
  const [message, setMessage] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingRecipientId, setEditingRecipientId] = useState<string | null>(null);
  const recipientGroupsKey = useMemo(() => recipientGroups.join('|'), [recipientGroups]);

  const selectedRecipients = useMemo(() => recipients.filter((recipient) => recipient.selected), [recipients]);
  const phoneErrors = useMemo(() => {
    return recipients.reduce<Record<string, string>>((acc, recipient) => {
      const phone = recipient.phone.trim();
      if (!phone) {
        acc[recipient.id] = 'El teléfono es obligatorio.';
        return acc;
      }

      if (!isValidPhoneFormat(phone)) {
        acc[recipient.id] = 'Formato inválido. Ej: +54 9 261 1234567';
      }

      return acc;
    }, {});
  }, [recipients]);

  const hasSelectedInvalidPhones = useMemo(
    () => selectedRecipients.some((recipient) => !!phoneErrors[recipient.id]),
    [selectedRecipients, phoneErrors],
  );
  const editingRecipient = useMemo(
    () => recipients.find((recipient) => recipient.id === editingRecipientId) ?? null,
    [recipients, editingRecipientId],
  );
  const editableUser = useMemo<Usuario | null>(() => {
    if (!editingRecipient?.linkedUserId) return null;

    return {
      id: editingRecipient.linkedUserId,
      name: editingRecipient.name,
      email: '',
      profile: {
        dni: editingRecipient.linkedProfile?.dni ?? null,
        telefono: editingRecipient.phone || (editingRecipient.linkedProfile?.telefono ?? null),
        direccion: editingRecipient.linkedProfile?.direccion ?? null,
      },
      roles: [],
      permissions: [],
    };
  }, [editingRecipient]);

  useEffect(() => {
    if (!open) return;

    const scopedGroups = (recipientGroupsKey ? recipientGroupsKey.split('|') : []) as RecipientGroup[];
    setRecipients(buildInitialRecipients(tramite, scopedGroups));
    setTemplate(null);
    setMessage(initialMessage);
    setPreviewMessage('');
    setPreviewError('');

    let cancelled = false;
    const loadTemplate = async () => {
      setLoadingTemplate(true);
      try {
        const response = await whatsappService.getTemplates({ event_type: eventType, is_active: true, page: 1 });
        if (cancelled) return;

        const firstTemplate = response.data?.[0] ?? null;
        setTemplate(firstTemplate);
        setMessage(firstTemplate?.body_template ?? initialMessage);
      } catch (error: any) {
        if (!cancelled) {
          errorAlert({ title: 'Error al cargar plantilla de WhatsApp', exception: error });
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplate(false);
        }
      }
    };

    void loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [open, eventType, tramite.id, initialMessage, recipientGroupsKey]);

  useEffect(() => {
    if (!open) {
      setEditingRecipientId(null);
    }
  }, [open]);

  const buildContext = () => {
    const firstRecipient = selectedRecipients.find((recipient) => recipient.name.trim() !== '') ?? selectedRecipients[0] ?? recipients[0];

    return {
      tramite_id: tramite.id,
      cliente_nombre: firstRecipient?.name?.trim() || '',
      link: '',
      ...contextOverrides,
    };
  };

  const handlePreview = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      setPreviewMessage('');
      setPreviewError('');
      return;
    }
    if (hasSelectedInvalidPhones) {
      errorAlert({ message: 'Corrige los teléfonos inválidos antes de previsualizar.' });
      return;
    }

    setLoadingPreview(true);
    setPreviewError('');
    try {
      const response = await whatsappService.previewMessage({
        event_type: eventType,
        message_body: trimmed,
        template_id: template?.id,
        context: buildContext(),
      });

      setPreviewMessage(response.rendered_message);
    } catch (error: any) {
      errorAlert({ title: 'Error al generar preview', exception: error });
      setPreviewError('No se pudo generar la previsualización. Intenta nuevamente.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    const recipientsPayload = selectedRecipients
      .map((recipient) => ({
        phone: recipient.phone.trim(),
        name: recipient.name.trim() || undefined,
      }))
      .filter((recipient) => recipient.phone !== '');
    const phones = recipientsPayload.map((recipient) => recipient.phone);

    if (!recipientsPayload.length) {
      errorAlert({ message: 'Debes seleccionar al menos un destinatario con teléfono válido.' });
      return;
    }
    if (hasSelectedInvalidPhones) {
      errorAlert({ message: 'Corrige los teléfonos inválidos antes de enviar.' });
      return;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      errorAlert({ message: 'El mensaje no puede estar vacío.' });
      return;
    }

    const confirmation = await questionAlert({
      title,
      text: `Se enviará a ${recipientsPayload.length} destinatario(s). ¿Confirmas?`,
      icon: 'question',
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmation?.isConfirmed) {
      return;
    }

    setSending(true);
    try {
      const response = await whatsappService.createDispatch({
        event_type: eventType,
        message_body: trimmed,
        phones,
        recipients: recipientsPayload,
        context: buildContext(),
      });

      successAlert({
        message: `Envío en cola (#${response.dispatch_id}). Total: ${response.totals.total}, inválidos: ${response.totals.invalid}, fallidos: ${response.totals.failed}.`,
      });

      onOpenChange(false);
    } catch (error: any) {
      errorAlert({ title: 'Error al crear el envío de WhatsApp', exception: error });
    } finally {
      setSending(false);
    }
  };

  const canEditRecipientProfile = (recipient: ComposerRecipient): boolean => {
    return Boolean(canManageUsers && recipient.isPersistable && recipient.linkedUserId);
  };

  const handleEditRecipientProfile = (recipient: ComposerRecipient) => {
    if (!canEditRecipientProfile(recipient)) return;
    setEditingRecipientId(recipient.id);
  };

  const handleSaveRecipientProfile = async (userId: number, profileData: UpdateUserProfileData) => {
    if (!editingRecipientId) return;

    const recipient = recipients.find((currentRecipient) => currentRecipient.id === editingRecipientId);
    const contextualProfileData: UpdateUserProfileData =
      recipient?.participantId && recipient?.participantType && recipient?.tramiteId
        ? {
            ...profileData,
            tramite_id: recipient.tramiteId,
            participant_type: recipient.participantType,
            participant_id: recipient.participantId,
          }
        : profileData;

    const response = await UserService.updateUserProfile(userId, contextualProfileData);
    const updatedUser = response.data?.user ?? {};
    const updatedProfile = response.data?.profile ?? {};
    const updatedName = typeof updatedUser.name === 'string' && updatedUser.name.trim() ? updatedUser.name.trim() : profileData.name?.trim() ?? '';
    const updatedPhone = typeof updatedProfile.telefono === 'string' ? updatedProfile.telefono : profileData.telefono ?? '';

    setRecipients((currentRecipients) =>
      currentRecipients.map((recipient) =>
        recipient.id === editingRecipientId
          ? {
              ...recipient,
              name: updatedName || recipient.name,
              phone: updatedPhone,
              linkedProfile: {
                dni: updatedProfile.dni ?? profileData.dni ?? null,
                telefono: updatedProfile.telefono ?? profileData.telefono ?? null,
                direccion: updatedProfile.direccion ?? profileData.direccion ?? null,
              },
            }
          : recipient,
      ),
    );

    await onTramiteUpdated?.();
    successAlert({ message: response.message });
    setEditingRecipientId(null);
  };

  return (
    <>
      <NotificationComposerModal
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        recipients={recipients}
        onRecipientsChange={setRecipients}
        message={message}
        onMessageChange={(value) => {
          setMessage(value);
          setPreviewError('');
        }}
        previewMessage={previewMessage}
        previewError={previewError}
        onPreview={handlePreview}
        onSend={handleSend}
        sending={sending}
        previewing={loadingPreview}
        loading={loadingTemplate}
        modeLabel={template ? `Plantilla activa: ${template.name} (editable)` : 'Modo mensaje custom'}
        phoneErrors={phoneErrors}
        onEditRecipientProfile={handleEditRecipientProfile}
        canEditRecipientProfile={canEditRecipientProfile}
      />

      <UserProfileDialog
        user={editableUser}
        open={!!editableUser}
        onClose={() => setEditingRecipientId(null)}
        onSave={handleSaveRecipientProfile}
      />
    </>
  );
};

export default WhatsAppEventModal;
